import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import React, { FC, MouseEvent, useState, useEffect } from 'react'
import MenuIcon from '@mui/icons-material/Menu'
import MuiMenu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MuiLink from '@mui/material/Link'
import { useLocation, Link } from 'react-router-dom'
import { HeaderOptionProps } from './useHeaderDetails'
import Divider from '@mui/material/Divider'
import ListItemIcon from '@mui/material/ListItemIcon'
import { PATIENTS, PROVIDERS } from '../../route.const'
import ClinicianSignIn from './ClinicianSignIn'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import SupervisedUserCircle from '@mui/icons-material/AccountCircle'
import { styled } from '@mui/system'
import Close from '@mui/icons-material/Close'
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material'
import { neutral } from '../../theme/base/theme'
import LogOutIcon from '@mui/icons-material/LogoutOutlined'
import LogInMenuData from '../../config-data/home/menu-list.json'
import ProfileAvatar from './ProfileAvatar'
import { useHeaderAuth } from './useHeaderAuth'
import { SIGN_IN_MENU_OPTIONS } from './constants'
import useAdobeAnalytics, {
  CLINICIAN_EVENTS,
  SHARED_EVENTS,
} from '@analytics/adobe'

// Styled component for MuiMenu
const MuiMenuStyled = styled(MuiMenu)({
  '& .MuiPaper-root': {
    width: '100%',
    maxWidth: 'none',
    left: '0px !important',
  },
})

// Main Menu component
const Menu: FC<{
  pages: HeaderOptionProps[]
  username: string
  isLogin: boolean
  degrees: string
  avatar: string
}> = ({ pages, username, isLogin, degrees, avatar }) => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)
  const placeholderElement = document.createElement('div')
  const [subAnchorElNav, setSubAnchorElNav] = useState<null | HTMLElement>(
    placeholderElement
  )
  const [subMenuOpen, setsubMenuOpen] = useState(true)
  const [activePage, setActivePage] = useState<string>('')
  const [openSignInDialog, setOpenSignInDialog] = useState(false)
  const [navigateURL, setNavigateURL] = useState('')
  const location = useLocation()
  const postLoginMenuItems = LogInMenuData.data

  const { handleLogout, handleLoginClick, trackLink, login, role } =
    useHeaderAuth()
  const { trackPage } = useAdobeAnalytics()

  // Handle opening the main navigation menu
  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget)
    setSubAnchorElNav(event.currentTarget)
    setsubMenuOpen(subAnchorElNav ? true : false)
  }

  // Handle closing the main navigation menu
  const handleCloseNavMenu = () => {
    setAnchorElNav(null)
    setSubAnchorElNav(placeholderElement)
    setsubMenuOpen(false)
  }

  // Handle opening/closing the sub-navigation menu
  const handleSubNavMenu = (event: MouseEvent<HTMLElement>) => {
    setSubAnchorElNav(subAnchorElNav ? null : event.currentTarget)
  }

  // Handle login click with dialog support - call original logic, then track
  const handleLoginWithDialog = (url: string, title: string) => {
    const onDialogOpen = () => {
      setOpenSignInDialog(true)
      handleCloseNavMenu()
      setNavigateURL(url)
    }

    // Call the original handleLoginClick which has all the core logic
    handleLoginClick(url, title, onDialogOpen)

    // Track AFTER the core logic has been initiated
    let tag = ''
    if (title && title.trim().toLowerCase() === 'clinician') {
      tag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_CLINICIAN
    } else if (title && title.trim().toLowerCase() === 'patient') {
      tag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_PATIENT
    }

    if (tag) {
      trackLink(
        {
          name: tag,
          location: 'unauthenticated-headers',
          type: 'url',
          destinationUrl: url,
        },
        {
          loginStatus: login ? 'logged in' : 'not authorized',
          role: role,
        }
      )
    }
  }

  // Handle closing all menus and performing logout
  const handleCloseAll = () => {
    handleLogout(username)
    handleCloseNavMenu()
  }

  // Update the active page based on the current location
  useEffect(() => {
    // Prefer an exact / startsWith match against provided page.path values
    const matched = pages.find(
      (page) => page.path && location.pathname === page.path
    )
    if (matched) {
      setActivePage(matched.label)
      return
    }

    // Fallbacks for legacy or special-case routes
    if (
      location.pathname.includes('search-result') ||
      location.pathname.includes('accesswall') ||
      location.pathname === '/providers'
    ) {
      // For these paths, activate "My matches" if it exists in pages
      const myMatchesPage = pages.find((p) => p.label === 'My matches')
      setActivePage(myMatchesPage?.label || pages[0]?.label || '')
    } else if (location.pathname.includes('profile')) {
      // try to find the profile page explicitly
      const profilePage = pages.find((p) => p.path?.includes('/profile'))
      if (profilePage) setActivePage(profilePage.label)
    } else if (location.pathname.includes('/survey/review')) {
      const surveyPage = pages.find((page) =>
        page.path?.includes('/survey/review')
      )
      if (surveyPage) {
        setActivePage(surveyPage.label)
      }
    } else if (location.pathname === '/') {
      setActivePage('Home')
    } else {
      // no matching page - clear active state
      setActivePage('')
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, pages])

  return (
    <>
      {openSignInDialog && (
        <ClinicianSignIn
          open={openSignInDialog}
          setOpen={setOpenSignInDialog}
          url={navigateURL}
        />
      )}
      <Box
        sx={{
          flexGrow: 1,
          display: { xs: 'flex', lg: 'none' },
          justifyContent: 'space-between',
        }}
      >
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="menu-appbar"
          id="menu-anchor"
          aria-haspopup="true"
          color={'primary'}
          onClick={handleOpenNavMenu}
        >
          {Boolean(anchorElNav) ? <Close /> : <MenuIcon />}
        </IconButton>
        <MuiMenuStyled
          anchorEl={anchorElNav}
          keepMounted
          open={Boolean(anchorElNav)}
          onClose={handleCloseNavMenu}
          sx={{
            display: { xs: 'block', lg: 'none' },
          }}
        >
          {pages.map((page) => {
            const keyBase = page.path || page.label
            return (
              <Box key={`mobile-page-${keyBase}`}>
                <MenuItem onClick={handleCloseNavMenu} sx={{ mt: -1, mb: -1 }}>
                  {page.type === 'link' ? (
                    <MuiLink
                      component={Link}
                      to={page.path || ''}
                      textAlign="center"
                      onClick={(e) => {
                        // Check if clinician is logged in
                        const isClinician = login && role === PROVIDERS

                        const label = page.label
                          .trim()
                          .toLowerCase()
                          .replace(/\s+/g, ' ')
                        let linkTag = ''
                        let pageTag = ''

                        // Force clinician sign-in tag for /providers/login or label 'Clinician'
                        switch (true) {
                          case !!(
                            page.path &&
                            page.path.toLowerCase() === '/providers/login'
                          ) || label === 'clinician':
                            linkTag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_CLINICIAN
                            break
                          case label.includes('sign in') ||
                            (page.path &&
                              page.path.toLowerCase().includes('sign-in')) ||
                            (page.path &&
                              page.path.toLowerCase().includes('login')):
                            linkTag = SHARED_EVENTS.LINK_CLICK.SIGN_IN
                            break
                          case label.includes('about us'):
                            linkTag = isClinician
                              ? CLINICIAN_EVENTS.NAV_CLICK.CL_ABOUT_US
                              : SHARED_EVENTS.NAV_CLICK.ABOUT_US
                            break
                          case label.includes('contact us') ||
                            (page.path &&
                              page.path.toLowerCase().includes('contact-us')):
                            linkTag = isClinician
                              ? CLINICIAN_EVENTS.NAV_CLICK.CL_CONTACT_US
                              : SHARED_EVENTS.NAV_CLICK.CONTACT_US
                            break
                          case label.includes('faq'):
                            linkTag = isClinician
                              ? CLINICIAN_EVENTS.NAV_CLICK.CL_FAQ
                              : SHARED_EVENTS.NAV_CLICK.FAQ
                            if (isClinician) {
                              pageTag = CLINICIAN_EVENTS.PAGE.FAQ
                            }
                            break
                          case label.includes('settings'):
                            linkTag = isClinician
                              ? CLINICIAN_EVENTS.NAV_CLICK.CL_SETTINGS
                              : SHARED_EVENTS.NAV_CLICK.SETTINGS
                            break
                          case label.includes('home'):
                            linkTag = isClinician
                              ? CLINICIAN_EVENTS.NAV_CLICK.CL_HOME
                              : SHARED_EVENTS.NAV_CLICK.LOGO
                            if (isClinician) {
                              pageTag = CLINICIAN_EVENTS.PAGE.HOME
                            }
                            break
                          case label.includes('privacy policy'):
                            if (isClinician) {
                              pageTag = CLINICIAN_EVENTS.PAGE.PRIVACY_POLICY
                            }
                            break
                          case label.includes('terms of use'):
                            if (isClinician) {
                              pageTag = CLINICIAN_EVENTS.PAGE.TERMS_OF_USE
                            }
                            break
                          case label.includes('clinical assistance'):
                            pageTag = CLINICIAN_EVENTS.PAGE.CLINICAL_ASSISTANCE
                            break
                          default:
                            linkTag = `om_nav_click:${label.replace(/\s+/g, '_')}`
                        }
                        // Never send 'Clinician' as the tag (force override)
                        if (
                          linkTag === 'Clinician' ||
                          linkTag === 'om_nav_click:clinician'
                        ) {
                          linkTag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_CLINICIAN
                        }

                        // Track link click event
                        if (linkTag) {
                          trackLink(
                            {
                              name: linkTag,
                              location: isClinician
                                ? 'authenticated-clinician-headers'
                                : 'header',
                              type: 'internal',
                            },
                            {
                              loginStatus: login
                                ? 'logged in'
                                : 'not authorized',
                              role: login ? role : undefined,
                            }
                          )
                        }

                        // Track page view event
                        if (pageTag) {
                          trackPage(
                            {
                              name: pageTag,
                              is404: false,
                            },
                            {
                              loginStatus: login ? 'logged in' : 'logged out',
                              userType: login ? role : undefined,
                            }
                          )
                        }
                      }}
                      sx={({ ues }) => ({
                        color: ues.neutral['80'],
                        fontWeight: 'bold',
                        fontSize: '18px',
                        lineHeight: '27px',
                        textDecoration: 'none',
                      })}
                    >
                      {page.label}
                    </MuiLink>
                  ) : (
                    page.label
                  )}
                </MenuItem>
                {login ? (
                  pages[pages.length - 1]?.label === page.label ? null : (
                    <Divider />
                  )
                ) : (
                  <Divider />
                )}
              </Box>
            )
          })}

          {login && (
            <>
              <Divider sx={{ pt: 1 }} />
              {/* Provider profile toggle */}
              {role === PROVIDERS && (
                <MenuItem onClick={handleSubNavMenu} key="provider-toggle">
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      mt: 1,
                    }}
                  >
                    <Button
                      sx={({ ues }) => ({
                        m: 0,
                        ml: -1,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: ues.primary['100'],
                        },
                      })}
                    >
                      <Box display={'flex'} whiteSpace={'normal'}>
                        <ProfileAvatar
                          avatar={avatar}
                          username={username}
                          size="small"
                        />
                        <Box
                          display={'flex'}
                          gap={1}
                          flexDirection={'column'}
                          ml={1}
                          width="250px"
                        >
                          <Typography
                            minWidth={'100px'}
                            component="div"
                            display="inline"
                            sx={{
                              display: 'block',
                              wordBreak: 'break-word',
                              textAlign: 'left',
                            }}
                          >
                            <Box display="inline">
                              <Box
                                component={Link}
                                display="inline"
                                sx={({ ues }) => ({
                                  marginBlockStart: 0,
                                  marginBlockEnd: 0,
                                  color: ues.neutral['80'],
                                  fontWeight: 'bold',
                                  fontSize: '18px',
                                  lineHeight: '27px',
                                  textDecoration: 'none',
                                })}
                              >{`${username}`}</Box>
                            </Box>
                            {degrees && (
                              <Typography display="inline">{`, ${degrees?.replace(
                                /[ ,]/g,
                                ', '
                              )}`}</Typography>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Button>
                    <ListItemIcon sx={{ mt: 1 }}>
                      <KeyboardArrowUp
                        sx={{
                          ml: 'auto',
                          display: subAnchorElNav ? 'block' : 'none',
                        }}
                      />
                      <KeyboardArrowDown
                        sx={{
                          ml: 'auto',
                          display: subAnchorElNav ? 'none' : 'block',
                        }}
                      />
                    </ListItemIcon>
                  </Box>
                </MenuItem>
              )}

              {/* Patient profile toggle */}
              {role === PATIENTS && (
                <Box
                  key="patient-toggle"
                  onClick={handleSubNavMenu}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    alignContent: 'center',
                  }}
                >
                  <Button
                    startIcon={
                      <SupervisedUserCircle
                        sx={{ height: '24px', width: '24px' }}
                      />
                    }
                    sx={({ ues }) => ({
                      m: 0,
                      mt: 0.5,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: ues.primary['100'],
                      },
                    })}
                  >
                    <Box
                      component={Link}
                      sx={({ ues }) => ({
                        marginBlockStart: 0,
                        marginBlockEnd: 0,
                        color: ues.neutral['80'],
                        fontWeight: 'bold',
                        fontSize: '18px',
                        lineHeight: '27px',
                        textDecoration: 'none',
                        width: '100%',
                      })}
                    >
                      {`${username}`}
                    </Box>
                  </Button>
                  <ListItemIcon sx={{ mt: 1.5, pr: 2 }}>
                    <KeyboardArrowUp
                      sx={{
                        ml: 'auto',
                        display: subAnchorElNav ? 'block' : 'none',
                      }}
                    />
                    <KeyboardArrowDown
                      sx={{
                        ml: 'auto',
                        display: subAnchorElNav ? 'none' : 'block',
                      }}
                    />
                  </ListItemIcon>
                </Box>
              )}
              {Boolean(subAnchorElNav) &&
                postLoginMenuItems.map((row) => {
                  const keyBase = row.path || row.label
                  if (!row.signOut) {
                    return (
                      <Box sx={{ pl: 2 }} key={`post-${keyBase}`}>
                        <MenuItem
                          sx={({ ues }) => ({
                            mt: { xs: 'auto', md: -1 },
                            mb: -1,
                            padding: 2,
                            pl: 5,
                            color: ues.neutral['80'],
                          })}
                        >
                          <MuiLink
                            component={Link}
                            sx={({ ues }) => ({
                              color: ues.neutral['80'],
                              '&:visited': {
                                color: neutral['80'],
                              },
                              textDecoration: 'none',
                              fontSize: '18px',
                              fontWeight: '400',
                              outline: 'none',
                              justifyContent: 'space-between',
                              display: 'flex',
                              gap: '4px',
                              width: '100%',
                            })}
                            onClick={() => {
                              setAnchorElNav(null)
                            }}
                            target={row.external ? '_blank' : '_self'}
                            rel={
                              row.external ? 'noopener noreferrer' : 'undefined'
                            }
                            to={
                              row.external
                                ? `${window.location.origin}${row.path}`
                                : row.path
                            }
                          >
                            {row.label}
                          </MuiLink>
                        </MenuItem>
                      </Box>
                    )
                  }
                  return (
                    <React.Fragment key={`post-signout-${keyBase}`}>
                      <Divider
                        component={'li'}
                        sx={{ display: subMenuOpen ? 'flex' : 'none', pt: 1 }}
                      />
                      <MenuItem
                        onClick={handleCloseAll}
                        sx={{
                          mt: { xs: 'auto', md: -1 },
                          mb: -1,
                          padding: 2,
                          pl: 7,
                          gap: 0.5,
                          display: subMenuOpen ? 'flex' : 'none',
                        }}
                      >
                        <LogOutIcon />
                        <Typography
                          variant="body3"
                          sx={{ fontSize: '18px', fontWeight: '400' }}
                        >
                          Sign out
                        </Typography>
                      </MenuItem>
                    </React.Fragment>
                  )
                })}
            </>
          )}
          {!login && (
            <Box key="signin-section">
              <MenuItem onClick={handleSubNavMenu} sx={{ mt: -1, mb: -1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 32,
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <MuiLink
                    textAlign="center"
                    sx={({ ues }) => ({
                      color: ues.neutral['80'],
                      fontWeight: 'bold',
                      fontSize: '18px',
                      lineHeight: '27px',
                      textDecoration: 'none',
                    })}
                  >
                    Sign in
                  </MuiLink>

                  <ListItemIcon>
                    <KeyboardArrowUp
                      sx={{
                        ml: 'auto',
                        display: subAnchorElNav ? 'block' : 'none',
                      }}
                    />
                    <KeyboardArrowDown
                      sx={{
                        ml: 'auto',
                        display: subAnchorElNav ? 'none' : 'block',
                      }}
                    />
                  </ListItemIcon>
                </Box>
              </MenuItem>
              {Boolean(subAnchorElNav) &&
                SIGN_IN_MENU_OPTIONS.map((row) => {
                  const keyBase = row.title || row.description
                  return (
                    <Box sx={{ pl: 2 }} key={`signin-${keyBase}`}>
                      <MenuItem
                        onClick={() => {
                          // Single unified function - executes core logic first, then tracks
                          handleLoginWithDialog(row.url, row.title)
                        }}
                        sx={({ ues }) => ({
                          flexDirection: 'row',
                          minWidth: '250px',
                          fontSize: '18px',
                          fontWeight: 400,
                          color: ues.neutral['80'],
                          '&:hover': {
                            color: `${ues.primary[70]} !important`,
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        })}
                      >
                        <Typography
                          variant="body3"
                          sx={{ fontSize: '18px', fontWeight: '400' }}
                        >
                          {row.description}
                        </Typography>
                      </MenuItem>
                    </Box>
                  )
                })}
            </Box>
          )}
        </MuiMenuStyled>
      </Box>
      <Box
        sx={{
          ml: -1.5,
          flexGrow: 1,
          gap: 1,
          display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
          mt: 0.5,
        }}
      >
        {pages.map((page) => {
          const keyBase = page.path || page.label
          return (
            <Box key={`desktop-page-${keyBase}`}>
              {page.type === 'link' ? (
                <MuiLink
                  component={Link}
                  to={page.path || ''}
                  onClick={(e) => {
                    setActivePage(e.currentTarget.innerText)
                    const originalLabel = e.currentTarget.innerText.trim()
                    // Map button labels to new tracking names
                    const getTrackingName = (label: string) => {
                      switch (label) {
                        case 'Dashboard':
                          return CLINICIAN_EVENTS.CLICK.DASHBOARD
                        case 'My profile':
                          return CLINICIAN_EVENTS.CLICK.MY_PROFILE
                        case 'Manage responses':
                          return CLINICIAN_EVENTS.CLICK.MANAGE_RESPONSES
                        default:
                          return e.currentTarget.innerText
                      }
                    }
                    // Check if clinician is logged in
                    const isClinician = login && role === PROVIDERS

                    const label = e.currentTarget.innerText
                      .trim()
                      .toLowerCase()
                      .replace(/\s+/g, ' ')
                    let linkTag = ''
                    let pageTag = ''

                    // Prevent duplicate event: if dropdown is open and click is for clinician, skip sending any event
                    if (
                      subMenuOpen &&
                      ((page.path &&
                        page.path.toLowerCase() === '/providers/login') ||
                        label === 'clinician')
                    ) {
                      return
                    }

                    // Only set om_link_click:sign_in_clinician for /providers/login
                    switch (true) {
                      case !!(
                        page.path &&
                        page.path.toLowerCase() === '/providers/login'
                      ):
                        linkTag = SHARED_EVENTS.LINK_CLICK.SIGN_IN_CLINICIAN
                        break
                      case label.includes('sign in') ||
                        (page.path &&
                          page.path.toLowerCase().includes('sign-in')) ||
                        (page.path &&
                          page.path.toLowerCase().includes('login')):
                        linkTag = SHARED_EVENTS.LINK_CLICK.SIGN_IN
                        break
                      case label.includes('about us'):
                        linkTag = isClinician
                          ? CLINICIAN_EVENTS.NAV_CLICK.CL_ABOUT_US
                          : SHARED_EVENTS.NAV_CLICK.ABOUT_US
                        break
                      case label.includes('contact us') ||
                        (page.path &&
                          page.path.toLowerCase().includes('contact-us')):
                        linkTag = isClinician
                          ? CLINICIAN_EVENTS.NAV_CLICK.CL_CONTACT_US
                          : SHARED_EVENTS.NAV_CLICK.CONTACT_US
                        break
                      case label.includes('faq'):
                        linkTag = isClinician
                          ? CLINICIAN_EVENTS.NAV_CLICK.CL_FAQ
                          : SHARED_EVENTS.NAV_CLICK.FAQ
                        if (isClinician) {
                          pageTag = pageTag || CLINICIAN_EVENTS.PAGE.FAQ
                        }
                        break
                      case label.includes('settings'):
                        linkTag = isClinician
                          ? CLINICIAN_EVENTS.NAV_CLICK.CL_SETTINGS
                          : SHARED_EVENTS.NAV_CLICK.SETTINGS
                        break
                      case label.includes('home'):
                        linkTag = isClinician
                          ? CLINICIAN_EVENTS.NAV_CLICK.CL_HOME
                          : SHARED_EVENTS.NAV_CLICK.LOGO
                        if (isClinician) {
                          pageTag = pageTag || CLINICIAN_EVENTS.PAGE.HOME
                        }
                        break
                      case label.includes('privacy policy'):
                        if (isClinician) {
                          pageTag =
                            pageTag || CLINICIAN_EVENTS.PAGE.PRIVACY_POLICY
                        }
                        break
                      case label.includes('terms of use'):
                        if (isClinician) {
                          pageTag =
                            pageTag || CLINICIAN_EVENTS.PAGE.TERMS_OF_USE
                        }
                        break
                      case label.includes('clinical assistance'):
                        pageTag =
                          pageTag || CLINICIAN_EVENTS.PAGE.CLINICAL_ASSISTANCE
                        break
                      default:
                        linkTag = login
                          ? role === PATIENTS
                            ? `om_pa_click:${label.replace(/\s+/g, '_') === 'my_survey' ? 'my_surveys' : label.replace(/\s+/g, '_')}`
                            : `om_cl_click:${label.replace(/\s+/g, '_') === 'my_survey' ? 'my_surveys' : label.replace(/\s+/g, '_')}`
                          : `om_nav_click:${label.replace(/\s+/g, '_')}`
                    }

                    if (role === PROVIDERS) {
                      const trackingName = getTrackingName(originalLabel)
                      if (trackingName !== originalLabel) {
                        trackLink(
                          {
                            name: trackingName,
                            location: CLINICIAN_EVENTS.PAGE.DASHBOARD,
                          },
                          {
                            loginStatus: login ? 'logged in' : 'logged out',
                            userType: role,
                          }
                        )
                      } else if (linkTag) {
                        // Fall back to general linkTag logic for other pages
                        trackLink(
                          {
                            name: linkTag,
                            location: 'authenticated-clinician-headers',
                            type: 'internal',
                          },
                          {
                            loginStatus: login ? 'logged in' : 'logged out',
                            userType: login ? role : undefined,
                          }
                        )
                      }
                    } else if (linkTag) {
                      // Determine location based on authentication status for patients
                      const analyticsLocation = login
                        ? 'authenticated patient - headers'
                        : 'unauthenticated-headers'

                      trackLink(
                        {
                          name: linkTag,
                          location: analyticsLocation,
                          type: 'internal',
                        },
                        {
                          loginStatus: login ? 'logged in' : 'logged out',
                          userType: login ? role : undefined,
                        }
                      )
                    }

                    // Track page view event
                    if (pageTag) {
                      trackPage(
                        {
                          name: pageTag,
                          is404: false,
                        },
                        {
                          loginStatus: login ? 'logged in' : 'logged out',
                          userType: login ? role : undefined,
                        }
                      )
                    }
                  }}
                  sx={({ ues }) => ({
                    color:
                      activePage === page.label
                        ? ues.primary['100']
                        : ues.neutral['80'],
                    fontWeight: 'bold',
                    fontSize: '16px',
                    lineHeight: '18px',
                    textDecoration: 'none',
                    borderBottom:
                      activePage === page.label
                        ? `2px solid ${ues.primary[100]}`
                        : 'none',
                    p: 1.5,
                    '&:hover': {
                      textDecorationColor: ues.primary['70'],
                      textDecoration:
                        activePage !== page.label ? 'underline' : 'none',
                    },
                  })}
                >
                  {page.label}
                </MuiLink>
              ) : (
                page.label
              )}
            </Box>
          )
        })}
      </Box>
    </>
  )
}

export default Menu
